/**
 * I mattoni delle schede: una sezione con titolo, una griglia di numeri, un
 * navigatore di mese, l'intestazione di un riquadro.
 *
 * Stanno qui e non dentro le singole viste perché ogni schermata deve
 * sembrare **una** cosa: quattro riquadri della stessa misura in cima, sotto
 * il dettaglio, ogni riquadro con lo stesso titolo nello stesso posto. Prima
 * ogni card decideva da sé titolo, padding e gerarchia, e l'insieme sembrava
 * un pannello di controllo montato a pezzi.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  formatMese,
  meseDi,
  spostaMese,
  todayInRome,
  type Mese,
} from "@otto/shared";
import { IconButton, Skeleton, cx } from "./primitives.tsx";

export function Sezione({
  icon,
  titolo,
  sottotitolo,
  azione,
  children,
}: {
  icon?: ReactNode;
  titolo: string;
  sottotitolo?: string;
  azione?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2 px-0.5">
        <div className="min-w-0">
          <h3 className="flex items-center gap-1.5 text-[15px] font-extrabold text-ink">
            {icon ? <span className="text-accent">{icon}</span> : null}
            {titolo}
          </h3>
          {sottotitolo ? (
            <p className="mt-0.5 text-[13px] leading-relaxed text-ink-soft">
              {sottotitolo}
            </p>
          ) : null}
        </div>
        {azione ? <div className="shrink-0">{azione}</div> : null}
      </header>
      {children}
    </section>
  );
}

/** Quattro su schermo largo, due su telefono: sempre della stessa misura. */
export function GrigliaKpi({ children }: { children: ReactNode }) {
  return (
    // Su telefono una colonna: in due, un numero come «7.419,50 €» finiva
    // tagliato con i puntini, e un conto tagliato non è un conto.
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {children}
    </div>
  );
}

export type TonoKpi = "plain" | "accent" | "ok" | "warn" | "danger";

const TONI_KPI: Record<TonoKpi, string> = {
  plain: "bg-surface ring-1 ring-line",
  accent: "bg-accent-soft ring-1 ring-accent-line",
  ok: "bg-ok-soft ring-1 ring-ok/15",
  warn: "bg-warn-soft ring-1 ring-warn/15",
  danger: "bg-danger-soft ring-1 ring-danger/15",
};

const VALORE_KPI: Record<TonoKpi, string> = {
  plain: "text-ink",
  accent: "text-accent-strong",
  ok: "text-ok",
  warn: "text-warn",
  danger: "text-danger",
};

const ICONA_KPI: Record<TonoKpi, string> = {
  plain: "bg-surface-muted text-ink-soft",
  accent: "bg-accent/10 text-accent",
  ok: "bg-ok/10 text-ok",
  warn: "bg-warn/10 text-warn",
  danger: "bg-danger/10 text-danger",
};

const BARRA_KPI: Record<TonoKpi, string> = {
  plain: "bg-accent",
  accent: "bg-accent",
  ok: "bg-ok",
  warn: "bg-warn",
  danger: "bg-danger",
};

export interface DeltaKpi {
  /** «+12%», «−3,4%»: già formattato. */
  testo: string;
  /** Se il segno è una buona notizia. Un costo che sale è `false`. */
  positivo: boolean | null;
}

export interface BarraKpi {
  /** Frazione piena, fra 0 e 1. */
  quota: number;
  /** I due estremi sotto la barra, «12» e «30» — o niente. */
  estremi?: [string, string];
}

/**
 * Un numero con la sua icona, la sua etichetta e il suo contesto.
 *
 * «Ti resta 1.574 €» da solo non dice niente: «Ti resta 1.574 € · +12%
 * sull'anno scorso» sì. Per questo il dettaglio è obbligatorio e il delta,
 * quando c'è, sta in alto a destra dove l'occhio lo cerca.
 */
export function Kpi({
  icon,
  etichetta,
  valore,
  dettaglio,
  delta,
  barra,
  tone = "plain",
  onClick,
  caricamento,
}: {
  icon?: ReactNode;
  etichetta: string;
  valore: string;
  dettaglio: ReactNode;
  delta?: DeltaKpi | null;
  barra?: BarraKpi | null;
  tone?: TonoKpi;
  onClick?: () => void;
  caricamento?: boolean;
}) {
  const contenuto = caricamento ? (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between">
        <Skeleton className="size-9 rounded-field" />
        <Skeleton className="h-5 w-14 rounded-pill" />
      </div>
      <Skeleton className="mt-1 h-3 w-24" />
      <Skeleton className="h-8 w-32" />
      <Skeleton className="h-3 w-28" />
    </div>
  ) : (
    <>
      {icon || delta ? (
        <div className="mb-3 flex items-center justify-between gap-2">
          {icon ? (
            <span
              className={cx(
                "grid size-9 shrink-0 place-items-center rounded-field",
                ICONA_KPI[tone],
              )}
            >
              {icon}
            </span>
          ) : (
            <span />
          )}
          {delta ? (
            <span
              className={cx(
                "inline-flex items-center gap-0.5 rounded-pill px-2 py-0.5 text-[11.5px] font-bold tabular-nums",
                delta.positivo === true && "bg-ok-soft text-ok",
                delta.positivo === false && "bg-danger-soft text-danger",
                delta.positivo == null && "bg-surface-muted text-ink-soft",
              )}
            >
              {delta.positivo === true ? (
                <ArrowUpRight size={12} />
              ) : delta.positivo === false ? (
                <ArrowDownRight size={12} />
              ) : null}
              {delta.testo}
            </span>
          ) : null}
        </div>
      ) : null}
      <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-ink-faint">
        {etichetta}
      </p>
      <p
        className={cx(
          "mt-1 truncate text-[28px] font-extrabold leading-none tabular-nums",
          VALORE_KPI[tone],
        )}
      >
        {valore}
      </p>
      {barra ? (
        <div className="mt-3">
          <div className="h-1.5 overflow-hidden rounded-pill bg-surface-sunken">
            <div
              className={cx("h-full rounded-pill", BARRA_KPI[tone])}
              style={{
                width: `${Math.round(Math.min(1, Math.max(0, barra.quota)) * 100)}%`,
              }}
            />
          </div>
          {barra.estremi ? (
            <div className="mt-1 flex justify-between text-[11px] tabular-nums text-ink-faint">
              <span>{barra.estremi[0]}</span>
              <span>{barra.estremi[1]}</span>
            </div>
          ) : null}
        </div>
      ) : null}
      <p className="mt-2 line-clamp-2 text-[13px] leading-snug text-ink-soft">
        {dettaglio}
      </p>
    </>
  );

  const classi = cx(
    "flex min-w-0 flex-col rounded-card p-4 text-left",
    TONI_KPI[tone],
    onClick &&
      "cursor-pointer transition-all duration-quick ease-soft hover:shadow-soft hover:ring-line-strong active:scale-[0.995]",
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={classi}>
        {contenuto}
      </button>
    );
  }
  return <div className={classi}>{contenuto}</div>;
}

/** ‹ settembre 2026 ›: uno solo per scheda, e tutto sotto lo segue. */
const MESI_CORTI = [
  "gen",
  "feb",
  "mar",
  "apr",
  "mag",
  "giu",
  "lug",
  "ago",
  "set",
  "ott",
  "nov",
  "dic",
];
/** Fin dove si torna indietro: prima del 2015 un affitto breve non l'aveva nessuno. */
const PRIMO_ANNO = 2015;

/**
 * Il mese, con le frecce per spostarsi di uno e un tocco sul nome per saltare.
 *
 * Le frecce bastavano per il mese prima. Per inserire i conti di novembre
 * 2023 servivano trenta tocchi (12/10/2026): toccando il nome si apre una
 * griglia con l'anno e i dodici mesi.
 */
export function NavigatoreMese({
  mese,
  onChange,
}: {
  mese: Mese;
  onChange: (mese: Mese) => void;
}) {
  const [aperto, setAperto] = useState(false);
  const [anno, setAnno] = useState(() => Number(mese.slice(0, 4)));
  const riquadro = useRef<HTMLDivElement | null>(null);
  const oggi = meseDi(todayInRome());
  const ultimoAnno = Number(oggi.slice(0, 4)) + 2;

  // Si apre sull'anno del mese che si sta guardando.
  useEffect(() => {
    if (aperto) setAnno(Number(mese.slice(0, 4)));
  }, [aperto, mese]);

  // Si chiude toccando fuori o con Esc.
  useEffect(() => {
    if (!aperto) return;
    const fuori = (e: PointerEvent) => {
      if (!riquadro.current?.contains(e.target as Node)) setAperto(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAperto(false);
    };
    document.addEventListener("pointerdown", fuori);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", fuori);
      document.removeEventListener("keydown", esc);
    };
  }, [aperto]);

  return (
    <div ref={riquadro} className="relative">
      <div className="flex items-center gap-1 rounded-pill bg-surface-muted p-1">
        <IconButton
          label="Mese precedente"
          onClick={() => onChange(spostaMese(mese, -1))}
        >
          <ChevronLeft size={15} />
        </IconButton>
        <button
          type="button"
          onClick={() => setAperto((a) => !a)}
          aria-expanded={aperto}
          aria-label={`Scegli il mese: ${formatMese(mese)}`}
          className="min-w-[124px] rounded-pill px-2 py-1 text-center text-[13.5px] font-bold capitalize text-ink transition-colors duration-quick hover:bg-surface"
        >
          {formatMese(mese)}
        </button>
        <IconButton
          label="Mese successivo"
          onClick={() => onChange(spostaMese(mese, 1))}
        >
          <ChevronRight size={15} />
        </IconButton>
      </div>

      {aperto ? (
        <div
          role="dialog"
          aria-label="Scegli il mese"
          className="animate-pop absolute left-0 top-full z-30 mt-2 w-[264px] rounded-card sm:left-1/2 sm:-translate-x-1/2 bg-surface p-3 shadow-lift ring-1 ring-line"
        >
          <div className="mb-2 flex items-center justify-between">
            <IconButton
              label="Anno precedente"
              onClick={() => setAnno((a) => Math.max(PRIMO_ANNO, a - 1))}
              disabled={anno <= PRIMO_ANNO}
              className="disabled:opacity-30"
            >
              <ChevronLeft size={15} />
            </IconButton>
            <span className="text-[14px] font-extrabold tabular-nums text-ink">
              {anno}
            </span>
            <IconButton
              label="Anno successivo"
              onClick={() => setAnno((a) => Math.min(ultimoAnno, a + 1))}
              disabled={anno >= ultimoAnno}
              className="disabled:opacity-30"
            >
              <ChevronRight size={15} />
            </IconButton>
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            {MESI_CORTI.map((nome, i) => {
              const questo =
                `${anno}-${String(i + 1).padStart(2, "0")}` as Mese;
              const scelto = questo === mese;
              return (
                <button
                  key={nome}
                  type="button"
                  onClick={() => {
                    onChange(questo);
                    setAperto(false);
                  }}
                  aria-pressed={scelto}
                  className={cx(
                    "rounded-field py-2 text-[13px] font-semibold capitalize transition-colors duration-quick",
                    scelto
                      ? "bg-ink text-ink-inverse"
                      : questo === oggi
                        ? "bg-surface-muted text-ink ring-1 ring-line-strong"
                        : "text-ink-soft hover:bg-surface-muted hover:text-ink",
                  )}
                >
                  {nome}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Il titolo di un riquadro: icona, nome, e a destra l'azione o la nota. */
export function IntestazioneRiquadro({
  icon,
  titolo,
  nota,
  azione,
}: {
  icon?: ReactNode;
  titolo: string;
  nota?: ReactNode;
  azione?: ReactNode;
}) {
  return (
    <header className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-0.5">
      {icon ? <span className="text-accent">{icon}</span> : null}
      <h3 className="text-[15px] font-extrabold text-ink">{titolo}</h3>
      {nota ? (
        <span className="ml-1 text-[12.5px] text-ink-faint">{nota}</span>
      ) : null}
      {azione ? <div className="ml-auto">{azione}</div> : null}
    </header>
  );
}
